// src/store/api/apiClient.ts
import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectAccessRole, ProjectRole } from '@app-types/access';

export interface Project {
  _id: string;
  projectId: string;
  name: string;
  slug: string;
  createdAt: string;
  createdBy: string;
  description: string;
  orgId: string;
  status?: 'active' | 'deleting';
  role?: ProjectRole;
  channelCount?: number;
  bundleCount?: number;
  membersCount?: number;
  storageBytes?: number;
  hasProductionChannel?: boolean;
}

export interface ProjectMember {
  userId: string;
  role: ProjectRole;
  isOrgAdmin?: boolean;
  isOwner?: boolean;
  isCurrentUser?: boolean;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    isDeleted?: boolean;
  };
}

export interface ProjectInviteSender {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isDeleted?: boolean;
}

export interface ProjectPendingInvitation {
  inviteId: string;
  inviteType: 'project';
  email: string;
  role: ProjectAccessRole;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  invitedBy: ProjectInviteSender | null;
}

export interface ProjectMemberListResponse {
  items: ProjectMember[];
  nextCursor: string | null;
  hasMore: boolean;
  pendingInvitations: ProjectPendingInvitation[];
}

export interface GetProjectMembersRequest {
  projectId: string;
  limit?: number;
  cursor?: string;
}

export interface SearchProjectMembersRequest {
  projectId: string;
  query?: string;
  name?: string;
  limit?: number;
  cursor?: string;
}

export interface ProjectCredentialsResponse {
  projectSlug: string;
  downloadApiKey: string;
  downloadKeyHint?: string;
}

export type UserPropertyValue = string | number | boolean;
export type UserPropertyType = 'string' | 'number' | 'boolean';

export interface UserPropertyDefinition {
  key: string;
  displayName: string;
  type: UserPropertyType;
  description: string;
  sampleValues: UserPropertyValue[];
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectUserPropertiesResponse {
  projectId: string;
  userProperties: UserPropertyDefinition[];
}

export interface UpdateProjectUserPropertiesRequest {
  projectId: string;
  userProperties: UserPropertyDefinition[];
}

export interface DeleteProjectUserPropertyRequest {
  projectId: string;
  userProperty: string;
}

export interface DeleteProjectUserPropertyResponse {
  projectId: string;
  removedUserProperty?: string;
  removed?: boolean;
  userProperties: UserPropertyDefinition[];
}

export interface UserPropertyAudienceResponse {
  totalInstalls: number;
  properties: Record<string, { total: number; values: Record<string, number> }>;
}

export interface UserPropertyValuesResponse {
  key: string;
  values: { value: UserPropertyValue; count: number }[];
}

export interface DeleteProjectResponse {
  projectId: string;
  orgId: string;
  status: 'deleting';
  cleanup: {
    mode: 'queued';
    jobId: string | null;
  };
}

export interface ProjectCleanupJobStatusResponse {
  jobId: string;
  state: string;
  isDone: boolean;
  isSuccess: boolean;
  data: {
    orgId: string;
    projectId: string;
    requestedByUserId: string;
    requestedAt: string;
  };
  attemptsMade: number;
  failedReason: string | null;
  result: unknown;
  timestamp: number | null;
  processedOn: number | null;
  finishedOn: number | null;
  projectStatus: 'active' | 'deleting' | 'deleted';
}

export interface ProjectTransferUser {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isDeleted?: boolean;
}

export interface ProjectTransferOrganizationSummary {
  orgId: string;
  name: string;
  slug: string;
}

export interface ProjectTransferProjectSummary {
  projectId: string;
  name: string;
  slug: string;
}

export interface ProjectTransfer {
  transferId: string;
  projectId: string;
  fromOrgId: string;
  toOrgId: string;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  project: ProjectTransferProjectSummary | null;
  fromOrganization: ProjectTransferOrganizationSummary | null;
  toOrganization: ProjectTransferOrganizationSummary | null;
  toUser: ProjectTransferUser | null;
  initiatedBy: ProjectTransferUser | null;
}

export interface ActiveProjectTransferResponse {
  transfer: ProjectTransfer | null;
}

export const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map(item => String(item ?? '').trim())
          .filter(Boolean)
      )
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return normalizeStringArray(parsed);
      } catch {
        // fall through to comma parsing
      }
    }

    return Array.from(
      new Set(
        trimmed
          .split(',')
          .map(item => item.trim())
          .filter(Boolean)
      )
    );
  }

  return [];
};

const normalizeUserPropertyValue = (value: unknown): UserPropertyValue | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'boolean') return value;
  return null;
};

export const normalizeUserPropertyDefinitions = (value: unknown): UserPropertyDefinition[] => {
  if (!Array.isArray(value)) return [];

  const definitions: UserPropertyDefinition[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
    const raw = item as Record<string, unknown>;
    const key = typeof raw.key === 'string' ? raw.key.trim() : '';
    if (!key) continue;
    const type: UserPropertyType =
      raw.type === 'number' || raw.type === 'boolean' || raw.type === 'string'
        ? raw.type
        : 'string';
    const displayName =
      typeof raw.displayName === 'string' && raw.displayName.trim()
        ? raw.displayName.trim()
        : key;
    const description = typeof raw.description === 'string' ? raw.description.trim() : '';
    const sampleValues = Array.isArray(raw.sampleValues)
      ? raw.sampleValues
          .map(normalizeUserPropertyValue)
          .filter((current): current is UserPropertyValue => current !== null)
      : [];

    definitions.push({
      key,
      displayName,
      type,
      description,
      sampleValues,
      firstSeenAt: typeof raw.firstSeenAt === 'string' ? raw.firstSeenAt : null,
      lastSeenAt: typeof raw.lastSeenAt === 'string' ? raw.lastSeenAt : null,
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : undefined,
      updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined,
    });
  }

  return definitions;
};

export const normalizeProjectMemberListResponse = (response: unknown): ProjectMemberListResponse => {
  if (Array.isArray(response)) {
    return {
      items: response as ProjectMember[],
      nextCursor: null,
      hasMore: false,
      pendingInvitations: [],
    };
  }

  const typed = response as Partial<ProjectMemberListResponse> | null | undefined;
  return {
    items: Array.isArray(typed?.items) ? typed.items : [],
    nextCursor: typed?.nextCursor ?? null,
    hasMore: Boolean(typed?.hasMore),
    pendingInvitations: Array.isArray(typed?.pendingInvitations)
      ? (typed.pendingInvitations as ProjectPendingInvitation[])
      : [],
  };
};

export const getProjectsByOrgTags = (result?: Project[]) =>
  result
    ? [
        ...result.map(project => ({
          type: 'Projects' as const,
          id: project.projectId || project._id,
        })),
        { type: 'Projects' as const, id: 'LIST' },
      ]
    : [{ type: 'Projects' as const, id: 'LIST' }];

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Projects', 'ProjectMembers', 'ProjectUserProperties', 'ProjectTransfers'],
  endpoints: builder => ({
    getProjectsByOrg: builder.query<Project[], string>({
      query: orgId => ({ url: '/projects/getAll', params: { orgId } }),
      providesTags: result => getProjectsByOrgTags(result),
    }),
    createProject: builder.mutation<
      { project: Project; downloadApiKey: string },
      { name: string; slug: string; description: string; orgId: string }
    >({
      query: body => ({
        url: '/projects/create',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    deleteProject: builder.mutation<DeleteProjectResponse, { projectId: string }>({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'Projects', id: projectId },
        { type: 'Projects', id: 'LIST' },
      ],
    }),
    getProjectCleanupStatus: builder.query<ProjectCleanupJobStatusResponse, string>({
      query: jobId => ({
        url: `/projects/cleanup-jobs/${jobId}`,
      }),
    }),
    initiateProjectTransfer: builder.mutation<
      ProjectTransfer,
      { projectId: string; targetOrgId: string }
    >({
      query: ({ projectId, targetOrgId }) => ({
        url: `/projects/${projectId}/transfers`,
        method: 'POST',
        data: { targetOrgId },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectTransfers', id: projectId },
      ],
    }),
    getActiveProjectTransfer: builder.query<ActiveProjectTransferResponse, string>({
      query: projectId => ({
        url: `/projects/${projectId}/transfers/active`,
      }),
      providesTags: (_result, _error, projectId) => [{ type: 'ProjectTransfers', id: projectId }],
    }),
    cancelProjectTransfer: builder.mutation<
      { transferId: string; projectId: string; status: 'canceled' },
      { projectId: string; transferId: string }
    >({
      query: ({ projectId, transferId }) => ({
        url: `/projects/${projectId}/transfers/${transferId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectTransfers', id: projectId },
      ],
    }),
    getProjectMembers: builder.query<ProjectMemberListResponse, GetProjectMembersRequest>({
      query: ({ projectId, limit, cursor }) => ({
        url: `/projects/${projectId}/members`,
        params: { limit, cursor },
      }),
      transformResponse: response => normalizeProjectMemberListResponse(response),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    searchProjectMembers: builder.query<ProjectMemberListResponse, SearchProjectMembersRequest>({
      query: ({ projectId, query, name, limit, cursor }) => ({
        url: `/projects/${projectId}/members/search`,
        params: { query, name, limit, cursor },
      }),
      transformResponse: response => normalizeProjectMemberListResponse(response),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    inviteProjectMember: builder.mutation<
      { message?: string },
      { projectId: string; email: string; role: ProjectAccessRole }
    >({
      query: ({ projectId, ...body }) => ({
        url: `/projects/${projectId}/members/invite`,
        method: 'POST',
        data: body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    updateProjectMemberRole: builder.mutation<
      { message?: string },
      { projectId: string; userId: string; role: ProjectAccessRole }
    >({
      query: ({ projectId, userId, role }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'PATCH',
        data: { role },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    revokeProjectMember: builder.mutation<{ message?: string }, { projectId: string; userId: string }>({
      query: ({ projectId, userId }) => ({
        url: `/projects/${projectId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    cancelProjectInvitation: builder.mutation<
      { inviteId: string; status: string },
      { projectId: string; inviteId: string }
    >({
      query: ({ projectId, inviteId }) => ({
        url: `/projects/${projectId}/members/invites/${inviteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectMembers', id: projectId },
        { type: 'ProjectMembers', id: 'LIST' },
      ],
    }),
    getProjectCredentials: builder.query<ProjectCredentialsResponse, string>({
      query: projectSlug => ({ url: `/projects/${projectSlug}/credentials` }),
    }),
    getProjectUserProperties: builder.query<ProjectUserPropertiesResponse, string>({
      query: projectId => ({ url: `/projects/${projectId}/user-properties` }),
      transformResponse: (
        response: { projectId?: string; userProperties?: unknown },
        _meta,
        projectId
      ) => ({
        projectId: response?.projectId || projectId,
        userProperties: normalizeUserPropertyDefinitions(response?.userProperties),
      }),
      providesTags: (_result, _error, projectId) => [
        { type: 'ProjectUserProperties', id: projectId },
      ],
    }),
    updateProjectUserProperties: builder.mutation<
      ProjectUserPropertiesResponse,
      UpdateProjectUserPropertiesRequest
    >({
      query: ({ projectId, userProperties }) => ({
        url: `/projects/${projectId}/user-properties`,
        method: 'PUT',
        data: {
          userProperties,
        },
      }),
      transformResponse: (
        response: { projectId?: string; userProperties?: unknown },
        _meta,
        { projectId }
      ) => ({
        projectId: response?.projectId || projectId,
        userProperties: normalizeUserPropertyDefinitions(response?.userProperties),
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectUserProperties', id: projectId },
      ],
    }),
    getUserPropertyAudience: builder.query<
      UserPropertyAudienceResponse,
      { projectId: string; channelId?: string }
    >({
      query: ({ projectId, channelId }) => ({
        url: `/projects/${projectId}/user-properties/audience`,
        params: channelId ? { channelId } : undefined,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectUserProperties', id: `${projectId}-audience` },
      ],
    }),
    getUserPropertyValues: builder.query<
      UserPropertyValuesResponse,
      { projectId: string; key: string; channelId?: string }
    >({
      query: ({ projectId, key, channelId }) => ({
        url: `/projects/${projectId}/user-properties/${encodeURIComponent(key)}/values`,
        params: channelId ? { channelId } : undefined,
      }),
    }),
    deleteProjectUserProperty: builder.mutation<
      DeleteProjectUserPropertyResponse,
      DeleteProjectUserPropertyRequest
    >({
      query: ({ projectId, userProperty }) => ({
        url: `/projects/${projectId}/user-properties/${encodeURIComponent(userProperty)}`,
        method: 'DELETE',
      }),
      transformResponse: (
        response: {
          projectId?: string;
          removedUserProperty?: string;
          removed?: boolean;
          userProperties?: unknown;
        },
        _meta,
        { projectId }
      ) => ({
        projectId: response?.projectId || projectId,
        removedUserProperty: response?.removedUserProperty,
        removed: response?.removed,
        userProperties: normalizeUserPropertyDefinitions(response?.userProperties),
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectUserProperties', id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectsByOrgQuery,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useLazyGetProjectCleanupStatusQuery,
  useInitiateProjectTransferMutation,
  useGetActiveProjectTransferQuery,
  useCancelProjectTransferMutation,
  useGetProjectMembersQuery,
  useSearchProjectMembersQuery,
  useInviteProjectMemberMutation,
  useUpdateProjectMemberRoleMutation,
  useRevokeProjectMemberMutation,
  useCancelProjectInvitationMutation,
  useLazyGetProjectCredentialsQuery,
  useGetProjectUserPropertiesQuery,
  useGetUserPropertyAudienceQuery,
  useLazyGetUserPropertyValuesQuery,
  useUpdateProjectUserPropertiesMutation,
  useDeleteProjectUserPropertyMutation,
} = projectsApi;
